import React, { Component } from "react";
import PropTypes from "prop-types";
import { Button, Col } from "react-bootstrap";
import {
  change,
  formValueSelector,
  Field,
  Fields,
  FormSection
} from "redux-form";
import { connect } from "react-redux";
import { debounce, trim } from "lodash";
import * as actions from "MODULES/parts/actions";
import {
  form,
  Form as AppForm,
  Check,
  CurrencyField,
  FormInput,
  FormRow,
  CageField,
  Select,
  NSNField,
  UNCodeField,
  CheckGroup,
  ECCNField,
  ScheduleBOrHTCodeField,
  AutoCompleteField
} from "MODULES/shared/components/Forms";
import { DashedSeparator } from "MODULES/shared/components";
import AuditedNoteTooltip from "MODULES/shared/components/NoteTooltip/AuditedNoteTooltip";
import {
  QUALITY_CONTROL,
  ADMIN,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  WAREHOUSE_MANAGER,
  WAREHOUSE_EXPERT,
  SALES,
  USER,
  CAN_ADD_UPDATE_PARTS,
  CAN_MERGE_PART_NUMBERS,
  ACCOUNTING
} from "MODULES/users/constants";
import countries from "LIB/countries";
import {
  AUTO_ADJUST_SRP_EXCLUSION,
  CAGE,
  CATALOG,
  CATALOG_URL,
  CLASSIFIED_MEMO,
  COUNTRY_OF_MFG,
  DESCRIPTION,
  DOM_LIFE_YEARS,
  DOM_SENSITIVE,
  ECCN,
  ESD,
  HTSUS_CODE,
  HTSUS_CODE_LABEL,
  IMPORTANT_MEMO,
  MANUFACTURER,
  MASTER_PART_LOCK,
  MLP,
  NSN,
  PART,
  PART_NUMBER,
  PERMANENT_EXTERNAL_NOTE_FOR_OQ,
  PURCHASED_AT,
  SCHEDULE_B_OR_HT_CODE,
  SPECIAL_CONTAINER_FEE,
  SPECIAL_CONTAINER_REQ,
  SPECIAL_CONTAINER_REQ_LABEL,
  UNITED_NATIONS_CODE,
  WARRANTY_OVERRIDE
} from "MODULES/parts/constants";
import Authorized from "MODULES/shared/components/Authorized";
import { ID } from "APP_ROOT/constants";
import { properCase } from "LIB/formatters/text";
import IconText from "MODULES/shared/components/IconText/IconText";
import {
  disableIfHasAnyOfSubroles,
  hasAnyOfRoles,
  hasPermission,
  hasRole,
  hasSubRole
} from "MODULES/users/helpers";
import { WARRANTY_OPTIONS } from "MODULES/inventories/constants";
import { arrayToOptions } from "LIB/forms";
import { onSuccess } from "LIB/submission";
import { useCurrentUser } from "APP_ROOT/hooks";
import api from "APP_ROOT/api";
import PartNumber from "../shared/PartNumber";
import Description from "../shared/Description";
import Warnings from "../CreatePart/Warnings";
import validate from "./validate";

const debounceTimeout = 350;
const formName = "editPart";

export class Form extends Component {
  state = {
    checkingForDuplicates: false,
    hasDuplicates: false
  };

  onCancel = () => {
    this.props.close();
    this.props.reset();
  };

  onDrop = e => {
    this.preventDefault(e);
    const dt = e.dataTransfer;
    const files = dt.files;

    this.setPreviewFile(files[0]);

    this.props.setFieldValue("file")(files[0]);
  };

  setPreviewFile = file => {
    if (!file) return null;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      this.setState({
        previewFile: reader.result
      });
    };

    return reader;
  };

  search = locationId => params =>
    api.locations.quickbooks.items
      .search(locationId, params)
      .then(({ data }) => data.results);

  preventDefault = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  removeCatalog = () => {
    this.setPreviewFile(null);
    this.props.setFieldValue("file")(null);
    this.props.setFieldValue(CATALOG_URL)(null);
    this.props.setFieldValue(CATALOG)(null);
  };
  check = debounce(partNumber => {
    const { search, id } = this.props;
    this.setState({ checkingForDuplicates: true });

    search(partNumber).then(results => {
      const hasDuplicates =
        results.length > 1 || (results.length === 1 && results[0].id !== id);

      this.setState({ checkingForDuplicates: false, hasDuplicates });
    });
  }, debounceTimeout);

  notify = () => this.setState({ hasDuplicates: false });

  resetScFee = scReq => {
    if (scReq === false) {
      this.props.change(SPECIAL_CONTAINER_FEE, null);
    }
  };

  submit = onSuccess(this.props.handleSubmit, this.onCancel, this.props);

  render() {
    const {
      mlpOnBlur,
      currentUser,
      domSensitive,
      getField,
      part,
      scReq,
      setFieldValue,
      submitting,
      toggleMergePartModal
    } = this.props;
    const catalogUrl = getField(CATALOG_URL);
    const { hasDuplicates, previewFile } = this.state;
    const { role, subrole } = currentUser;
    const disabledIfWhDirector = disableIfHasAnyOfSubroles(currentUser, [
      WAREHOUSE_LOGISTICS_DIRECTOR
    ]);
    const disabledIfWhManager = disableIfHasAnyOfSubroles(currentUser, [
      WAREHOUSE_EXPERT,
      WAREHOUSE_MANAGER
    ]);
    const allowedToEdit =
      hasAnyOfRoles(currentUser, [QUALITY_CONTROL, ADMIN]) ||
      hasSubRole(currentUser, WAREHOUSE_LOGISTICS_DIRECTOR) ||
      hasPermission(currentUser, CAN_ADD_UPDATE_PARTS);

    const allowedToEditWarrantyOptions =
      role === ADMIN ||
      hasRole(currentUser, SALES) ||
      hasPermission(currentUser, CAN_ADD_UPDATE_PARTS);

    const allowedToEditDescription =
      allowedToEdit || hasRole(currentUser, SALES);

    const countryOptions = countries.map(c => ({
      value: c.code,
      text: c.name
    }));

    return (
      <AppForm
        {...this.props}
        submit={this.submit}
        submitting={submitting || this.state.checkingForDuplicates}
      >
        <AppForm.Body>
          {hasDuplicates && (
            <div className="alert alert-warning">
              <strong>Heads up!</strong> A base part number matching this part
              number already exists!
            </div>
          )}
          <FormRow>
            <Col md={4}>
              <Fields
                component={PartNumber}
                names={[PART_NUMBER, DESCRIPTION]}
                notify={this.notify}
                check={this.check}
                disabled={
                  !allowedToEdit || disabledIfWhManager || disabledIfWhDirector
                }
              />
            </Col>
            <Col md={4}>
              <Fields
                component={Description}
                names={[PART_NUMBER, DESCRIPTION]}
                disabled={!allowedToEditDescription}
              />
            </Col>
            <Col md={4}>
              <Field name={MANUFACTURER} component={FormInput} label="MFG" />
            </Col>
          </FormRow>
          <hr className="hr-sm margin-top-none" />
          <FormRow>
            <Col md={2}>
              <CageField name={CAGE} component={FormInput} label={CAGE} />
            </Col>
            <Col md={2}>
              <ECCNField name={ECCN} component={FormInput} label={"ECCN"} />
            </Col>
            <Col md={3}>
              <UNCodeField
                name={UNITED_NATIONS_CODE}
                component={FormInput}
                label="UN Code"
              />
            </Col>
            <Col md={5}>
              <NSNField
                name={NSN}
                component={FormInput}
                label="NSN"
                disabled={disabledIfWhManager}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={6} xs={12}>
              <ScheduleBOrHTCodeField
                name={SCHEDULE_B_OR_HT_CODE}
                component={FormInput}
                label="Schedule B or HT Code"
              />
            </Col>
            <Col md={6} xs={12}>
              <Field
                name={HTSUS_CODE}
                component={FormInput}
                label={HTSUS_CODE_LABEL}
                format={value => trim(value)}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={3}>
              <CurrencyField
                name={MLP}
                label="Manufacturer List Price"
                onBlur={(evt, val) => mlpOnBlur(evt, val)}
                required={getField(PURCHASED_AT)}
                disabled={disabledIfWhManager}
              />
            </Col>
            <Col md={4}>
              <Field
                name={PURCHASED_AT}
                component={FormInput}
                label="Date of Price"
                type="date"
                required={getField(MLP)}
                disabled={disabledIfWhManager}
              />
            </Col>
            <Col md={5}>
              <Field
                component={Select}
                options={countryOptions}
                name={COUNTRY_OF_MFG}
                label="Ctry of Mfg"
              />
            </Col>
          </FormRow>
          <DashedSeparator />
          <Warnings subrole={subrole} formName={formName} />
          <FormRow>
            <Col md={6}>
              <CheckGroup
                justified
                title={SPECIAL_CONTAINER_REQ_LABEL}
                info="A special container fee note will be added to outgoing quote and sales order confirmation emails."
              >
                <Field
                  onChange={(ev, val) => this.resetScFee(val)}
                  component={Check}
                  name={SPECIAL_CONTAINER_REQ}
                  label={SPECIAL_CONTAINER_REQ_LABEL}
                  icon="fa-cube"
                />
              </CheckGroup>
            </Col>
            <Col md={3}>
              {scReq && (
                <CurrencyField
                  name={SPECIAL_CONTAINER_FEE}
                  label={properCase(SPECIAL_CONTAINER_FEE)}
                  required
                />
              )}
            </Col>
            <Col md={3}>
              <CheckGroup
                justified
                title={ESD.toUpperCase()}
                info="Notifies receiving dept"
              >
                <Field component={Check} name={ESD} label={ESD.toUpperCase()} />
              </CheckGroup>
            </Col>
          </FormRow>
          <DashedSeparator />
          <FormRow>
            <Col md={4}>
              <Field
                component={Select}
                options={arrayToOptions(WARRANTY_OPTIONS)}
                name={WARRANTY_OVERRIDE}
                label={[
                  properCase(WARRANTY_OVERRIDE),
                  <IconText
                    iconSize="fa-stack-1x"
                    icon="fa-info"
                    note="Choose default warranty period for this part number."
                  />
                ]}
                disabled={!allowedToEditWarrantyOptions}
              />
            </Col>
            <Col md={4}>
              <div className="form-group">
                <label
                  htmlFor={AUTO_ADJUST_SRP_EXCLUSION}
                  className="control-label"
                >
                  {properCase(AUTO_ADJUST_SRP_EXCLUSION)}
                  <IconText
                    iconSize="fa-stack-1x"
                    icon="fa-info"
                    note="Excludes part from auto-adjusting SRP on anniversary dates.  Notifications for review will remain in place."
                  />
                </label>
                <br />
                <div
                  className="btn-group btn-group-justified"
                  data-toggle="buttons"
                >
                  <Field
                    component={Check}
                    name={AUTO_ADJUST_SRP_EXCLUSION}
                    label={properCase(AUTO_ADJUST_SRP_EXCLUSION)}
                  />
                </div>
              </div>
            </Col>
            <Col md={4}>
              <CheckGroup
                justified
                title={properCase(MASTER_PART_LOCK)}
                info="Disallows EXACT alternates from being added as main part numbers."
              >
                <Field
                  component={Check}
                  name={MASTER_PART_LOCK}
                  label={properCase(MASTER_PART_LOCK)}
                  disabled={role !== ADMIN}
                />
              </CheckGroup>
            </Col>
          </FormRow>
          <FormRow>
            <Col md={4}>
              <CheckGroup justified>
                <div className="form-group">
                  <Field
                    component={Check}
                    name={DOM_SENSITIVE}
                    label={"DOM Sensitive"}
                    icon="fa-calendar"
                  />
                </div>
              </CheckGroup>
            </Col>
            <Col md={4}>
              {domSensitive && (
                <div className="form-group">
                  <Field
                    name={DOM_LIFE_YEARS}
                    component={FormInput}
                    label={[
                      properCase(DOM_LIFE_YEARS),
                      <IconText
                        iconSize="fa-stack-1x"
                        icon="fa-info"
                        note="For POs, manager approval required if less than 60% life remaining."
                      />
                    ]}
                    required={domSensitive}
                  />
                </div>
              )}
            </Col>
          </FormRow>
          <DashedSeparator />
          <FormRow>
            <Col md={12}>
              <Field
                name={PERMANENT_EXTERNAL_NOTE_FOR_OQ}
                component={FormInput}
                label="Permanent External Note For OQ"
                disabled={disabledIfWhManager}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={12}>
              <Field
                name={IMPORTANT_MEMO}
                component={FormInput}
                label={properCase(IMPORTANT_MEMO)}
                disabled={disabledIfWhManager}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={12}>
              <Field
                name={CLASSIFIED_MEMO}
                component={FormInput}
                label={properCase(CLASSIFIED_MEMO)}
                disabled={disabledIfWhManager}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={12}>
              <label className="control-label" htmlFor={CATALOG_URL}>
                Catalog Photo
                {catalogUrl && (
                  <IconText
                    iconSize="fa-stack-1x"
                    icon="fa-info"
                    note="To Replace, Drag & Drop New Image"
                  />
                )}
              </label>
              <div
                style={{
                  border: "1px dashed #FF9800",
                  height: "200px",
                  position: "relative"
                }}
                onDrop={this.onDrop}
                onDragOver={this.preventDefault}
              >
                {((previewFile || catalogUrl) && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translateX(-50%) translateY(-50%)"
                    }}
                  >
                    <div style={{ position: "relative" }}>
                      <span
                        className="fa fa-times clickable"
                        onClick={this.removeCatalog}
                        style={{
                          position: "absolute",
                          right: "-13px",
                          top: "-13px"
                        }}
                      />
                      <img
                        alt={CATALOG}
                        src={previewFile || catalogUrl}
                        style={{
                          maxHeight: "140px"
                        }}
                      />
                    </div>
                  </div>
                )) || (
                  <p
                    style={{
                      maxHeight: "140px",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translateX(-50%) translateY(-50%)",
                      color: "#FF9800"
                    }}
                  >
                    Drop an Image
                  </p>
                )}
              </div>
              {(previewFile || catalogUrl) && (
                <React.Fragment>
                  <span>Catalog:</span>
                  {part.catalog_audit && (
                    <AuditedNoteTooltip obj={part} field="catalog" />
                  )}
                </React.Fragment>
              )}
            </Col>
          </FormRow>
          {hasRole(currentUser, ACCOUNTING) &&
            useCurrentUser()
              .locations.filter(f => f.quickbook_realm_id)
              .map((l, idx) => [
                <FormRow>
                  <Col sm={12}>
                    <FormSection name={`locations[${idx}]`}>
                      <Field
                        component={FormInput}
                        name="id"
                        type="hidden"
                        style={{ height: 0 }}
                      />
                      <Field
                        name={"quickbooks_account"}
                        label={`Quickbooks Account - ${l.entity_name}`}
                        placeholder="Search by typing an account's name"
                        component={AutoCompleteField}
                        onItemSelected={item =>
                          setFieldValue(`locations[${idx}].quickbooks_account`)(
                            item
                          )}
                        selectedItem={part.quickbooks_account[l.id]}
                        search={this.search(l.id)}
                        formatItem={result => result && result.name}
                      />
                    </FormSection>
                  </Col>
                </FormRow>
              ])}
        </AppForm.Body>
        <AppForm.AdditionalButtons>
          <Authorized permissions={[CAN_MERGE_PART_NUMBERS]}>
            <Button
              onClick={() => {
                toggleMergePartModal(true);
                this.props.close();
              }}
              className="pull-left btn-danger"
            >
              Merge Part
            </Button>
          </Authorized>
        </AppForm.AdditionalButtons>
      </AppForm>
    );
  }
}

Form.propTypes = {
  domSensitive: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string.isRequired,
    subrole: PropTypes.string.isRequired
  }),
  mlpOnBlur: PropTypes.func.isRequired,
  search: PropTypes.func.isRequired,
  scReq: PropTypes.bool,
  submitting: PropTypes.bool.isRequired
};

const selector = formValueSelector(formName);

export default connect(
  state => ({
    id: state.partDetail.getIn([PART, ID]),
    getField: name => selector(state, name),
    currentUser: state.session.get(USER).toJS(),
    scReq: selector(state, SPECIAL_CONTAINER_REQ),
    domSensitive: selector(state, DOM_SENSITIVE)
  }),
  dispatch => ({
    onSubmit: data => dispatch(actions.update(data)),
    search: partNumber =>
      dispatch(actions.searchMatchingPartNumbers(partNumber)),
    mlpOnBlur: (evt, val) => {
      if (parseFloat(val) <= 0) {
        evt.preventDefault();
        dispatch(change(formName, PURCHASED_AT, null));
        dispatch(change(formName, MLP, null));
      }
    },
    setFieldValue: field => val => dispatch(change(formName, field, val))
  })
)(
  form({
    form: formName,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/parts/components/EditPart/Form.jsx